/**
 * THE LIVE RUN, WATCHED FROM ONE PLACE — the browser half of `GET
 * /declarations/{id}/run`.
 *
 * A confirm gesture returns as soon as the value is committed; the cone it fires is a
 * detached task that can take minutes. This hook is what turns that into something the
 * screens can show: `recompute_started` announces the cone VERBATIM (per-node status
 * cannot — a hash-skipped node never flips to running), `recompute_settled` releases
 * it, and `value_changed` names the addresses that actually moved.
 *
 * THE CURSOR CONTRACT IS THE ENDPOINT'S, honoured literally: start at 0, hand `cursor`
 * back on every request, and never take "is it over" from anywhere else — a poller that
 * trusts a second read can stop holding a prefix of the feed and never learn the rest.
 *
 * THE CURSOR IS KEPT ACROSS MOUNTS, with the fold that produced it. A guided hop
 * unmounts mission control and mounts a step screen; a per-mount cursor would restart at
 * 0 and replay the whole event history on every hop. The cursor and the open-cone map
 * are ONE unit — the map is built by folding balanced bookends from seq 0, so keeping
 * the cursor without its fold would lose a lock whose `recompute_started` was seen
 * before the hop and leave a cone reading as editable while it recomputes. One run at a
 * time: a different declaration replaces the entry outright, so a stale cursor can never
 * be read against another run's feed.
 *
 * IT DOES NOT POLL WHEN NOTHING IS HAPPENING. A page that polls forever is not
 * acceptable, so the loop runs only while a cone is in flight or the matrix still holds
 * unsettled rows, plus a short grace after a gesture (the request has returned but the
 * detached task may not have emitted its bookend yet). A HIDDEN TAB polls nothing at
 * all: nobody is looking, and the poll is the run's whole read amplification.
 *
 * LOCKS ARE PER UNIT, NEVER PER SCREEN. What this exposes is the set of KINDS the
 * announced cones cover; a screen maps the fields IT renders onto kinds through the
 * read model's `field_producers` (`unitLocked` below) and dims only those units. The
 * board goes one finer and asks per INSTANCE (`isInFlight`), which is what makes one
 * dot pulse while its forty-seven siblings sit still. Navigation is never blocked.
 */
import { type AxiosRequestConfig, isCancel } from "axios"
import { useCallback, useEffect, useRef, useState } from "react"
import { apiClientV2 } from "../../../../../../services/api/apiClient"
import type { RunNode } from "./runProgress"

/** One entry of the run feed, verbatim (`RunEventOut`). */
export interface RunEvent {
  seq: number
  kind: string
  node_id?: string | null
  cause_node?: string | null
  cone?: string[] | null
}

interface RunPayload {
  declaration_id: string
  /** The COMPLETE matrix every time — state, not a delta. Cell-keyed rows arrive in
   *  the PUBLIC article-number space (the route translates them). */
  nodes: RunNode[]
  events: RunEvent[]
  cursor: number
  scope_article_count?: number | null
  large_scope?: boolean
}

export interface RunPollState {
  /** The whole node matrix as of the last poll — aggregates and fan instances alike. */
  nodes: RunNode[]
  /** True once a poll has returned for this declaration — tells "nothing yet" from
   *  "nothing at all", which is what keeps a screen from calling a live run manual for
   *  one cycle. */
  polled: boolean
  /** The kinds every in-flight cone announced — what is locked right now. */
  lockedKinds: Set<string>
  /** Node ids whose value moved while this hook was watching (`kind` or `kind:key`). */
  changedNodes: Set<string>
  /** How many articles this run is checking against, once the run itself has
   *  resolved a supplier. The declaration row cannot answer this while the run is
   *  going (its `supplier_id` column is committed only at the end), which is why the
   *  poll carries it. Null until a supplier is resolved. */
  scopeArticleCount: number | null
  /** `scopeArticleCount` past the server's threshold: the wait is long enough that
   *  the screen should say why. */
  largeScope: boolean
  /** True while at least one announced cone has not settled. */
  inFlight: boolean
  /**
   * True while the feed is WAITING FOR A SETTLE it knows is coming: a cone is open,
   * or a requested recompute (a gesture's cone, or the final review queued on the
   * worker, `review:…`) has no walk covering it yet. Unlike `inFlight` it is true
   * from the request on, before the walk starts, which on the worker can be minutes.
   * False whenever the loop is not running (stopped, failed, hidden): then nobody
   * would ever clear it, and a caller must not wait on a feed nobody reads.
   */
  waiting: boolean
  /** Is THIS instance recomputing (its row is queued or running)? The precise per-cell
   *  signal: a hash-skipped instance never flips, so only genuinely-busy units read as
   *  busy. */
  isInFlight: (kind: string, key?: string) => boolean
  /** Start watching. Called by a gesture the instant its request returns. */
  watch: () => void
  /**
   * HOW MANY CONES HAVE SETTLED while this hook was watching. Monotonic, so a
   * caller re-reads by depending on it — which is the whole point: a cone
   * settling is the moment the pipeline has RE-PUBLISHED, and every
   * interface-backed query is stale from then until it is refetched. A boolean
   * could not express "and then another one settled".
   */
  settledCount: number
}

/** How often to ask while something is in flight. */
const INTERVAL_MS = 1200

/** How many idle polls to spend waiting for a just-fired cone to announce itself
 *  before concluding that the gesture moved nothing (a hash-skipped commit fires no
 *  cone at all, and that is a normal outcome, not a lost event). */
const GRACE_POLLS = 8
/** Persistent-mode retry ceiling after a failed request, and the ceiling a
 *  silent open cone backs off to. */
const MAX_BACKOFF_MS = 15_000
/**
 * A NON-PERSISTENT LOOP IS BOUNDED BY TIME SINCE ITS LAST GESTURE, never by what the
 * feed says. "A cone is open" and "a row is unsettled" can both stay true forever (a
 * walk killed by a deploy writes no `recompute_settled`; a row can be left `running`
 * or flip status indefinitely), and a real walk can go many minutes without a single
 * event (the recorder is silent for succeeding nodes). So, from each `watch()`:
 * back off 1.2 s doubling to 15 s for the first BACKOFF_PHASE_MS, then a heartbeat
 * every HEARTBEAT_MS, and stop at CEILING_MS whatever the state. Worst case per
 * gesture: ~43 requests of backoff + ~20 heartbeats ≈ 63 requests. The next gesture,
 * a window focus, or the tab becoming visible again re-arms it.
 *
 * Each answer is the node matrix's STATUSES (`view=status`, no outputs), and an
 * unchanged run answers 304 with no body (see `lastAnswer`).
 */
const BACKOFF_PHASE_MS = 10 * 60_000
const HEARTBEAT_MS = 60_000
const CEILING_MS = 30 * 60_000

/**
 * THE CURSOR AND ITS FOLD, kept across mounts. Module state, deliberately: the two
 * screens a guided hop moves between are siblings, so there is no component that
 * outlives both and could hold this.
 */
interface RunCursor {
  declId: string
  /** The highest event seq this client has folded. */
  lastSeq: number
  /** cause node → the cone kinds it announced, for the recomputes still in flight. */
  open: Map<string, string[]>
  /** How many `recompute_settled` bookends have been folded into this cursor, by
   *  ANY hook. An event is delivered to exactly one poller (whichever advances the
   *  cursor past it), so two hooks watching the same declaration at once — the review
   *  page's refetch feed and the footer's approve retry — would otherwise split the
   *  settles between them and each miss the other's. Each hook reports the growth of
   *  this count since it first watched, not only the settles its own requests carried. */
  settled: number
  /**
   * Cone kind → seqs of the `recompute_requested` events no walk has covered yet. The
   * request is written BEFORE the walk waits for its cone slot and declaration lock,
   * so on a large declaration it can sit for minutes with no `recompute_started`; a
   * loop that only watched `open` would spend its grace and stop inside that gap and
   * never see the settle. Folded like the backend's `recompute_state_of`: per KIND
   * (coalescing walks a later gesture's cause), and a settle closes only the
   * requests made before its own walk started.
   */
  requested: Map<string, number[]>
  /** cause node → seq of its latest `recompute_started`: the bound a settle closes
   *  requests below. */
  startedSeq: Map<string, number>
  /** Seq of the latest `run_completed` folded (0: none). */
  completedSeq: number
  /** Seq of the latest extraction-walk event folded (`node_*`): a walk event after the
   *  latest `run_completed` means a newer run has started since. */
  walkSeq: number
}
let sharedCursor: RunCursor | null = null

function cursorFor(declId: string): RunCursor {
  if (!sharedCursor || sharedCursor.declId !== declId) {
    sharedCursor = {
      declId,
      lastSeq: 0,
      open: new Map(),
      settled: 0,
      requested: new Map(),
      startedSeq: new Map(),
      completedSeq: 0,
      walkSeq: 0,
    }
  }
  return sharedCursor
}

/**
 * THE LAST ANSWER AND ITS TAG (TUR-1725), kept like the cursor: one declaration at a time.
 * The poll asks `view=status` — the matrix without node outputs, which no screen reads —
 * and the server tags that answer with a weak ETag over everything it is made of. Sent
 * back as `If-None-Match`, an unchanged run answers 304 with no body and costs neither
 * side the matrix. An older backend ignores `view` and sends no tag: nothing is ever sent
 * back then, and the full answer (a superset) is read as before.
 */
let lastAnswer: { declId: string; etag: string; payload: RunPayload } | null =
  null
/** The request in flight per declaration AND cursor: two hooks watching one declaration
 *  (the page and the footer) share it instead of each downloading the same matrix, but
 *  a hook asking from another cursor (one rebuilt at 0, say) never joins a request that
 *  answers a different question. */
const inFlightRuns = new Map<string, Promise<RunPayload>>()

/** Test seam: forget the shared cursor, so one test's feed cannot be read against
 *  another's. Never called by the app — a real client keeps the cursor on purpose. */
export function resetRunCursor(): void {
  sharedCursor = null
  lastAnswer = null
  inFlightRuns.clear()
}

/** The extraction walk's own per-node events (the backend's `_ATTEMPT_EVENT_KINDS`
 *  without the bookend): one after the latest `run_completed` is a newer run. */
const WALK_EVENTS = new Set([
  "node_queued",
  "node_started",
  "node_ok",
  "node_flagged",
  "node_failed",
])

/** The cone kind of a cause node (`resolve_scope:reach:0` → `resolve_scope`). */
const coneKind = (cause: string) => cause.split(":", 1)[0]

/** Is a requested recompute still waiting for the walk that covers it? */
const hasPendingRequest = (cursor: RunCursor) =>
  [...cursor.requested.values()].some((seqs) => seqs.length > 0)

async function requestRun(
  declId: string,
  afterSeq: number,
  etag: string | null,
): Promise<{ status: number; data: unknown; etag: string | null }> {
  const response = await apiClientV2.get(
    `/material-compliance/declarations/${declId}/run`,
    {
      params: { after_seq: afterSeq, view: "status" },
      headers: etag ? { "If-None-Match": etag } : undefined,
      // 304 is an answer here (the run has not moved), not an error.
      validateStatus: (status: number) =>
        (status >= 200 && status < 300) || status === 304,
      // The feed's keys are the pipeline's own (`node_id`, `cause_node`, `cone`) and
      // the node matrix is keyed by kind — the camel transform would rewrite both.
      skipCaseConversion: true,
    } as AxiosRequestConfig & { skipCaseConversion?: boolean },
  )
  const headers = (response.headers ?? {}) as Record<string, unknown>
  const tag = headers.etag ?? headers.ETag
  return {
    status: response.status ?? 200,
    data: response.data,
    etag: typeof tag === "string" && tag ? tag : null,
  }
}

/** A request that got no HTTP answer at all (network error, or a CORS preflight the
 *  server refused), as opposed to one the server answered with an error status. */
const noHttpAnswer = (error: unknown) =>
  !isCancel(error) &&
  typeof (error as { response?: { status?: unknown } } | null)?.response
    ?.status !== "number"

/**
 * One poll. A 304 is the last answer again, WHOLE: the SAME nodes (the same array, so a
 * screen re-renders nothing), the SAME events and the same cursor (the tag covers
 * `after_seq`, so a 304 can only answer the cursor the last answer was for). The events
 * go out again on purpose: the answer is stored when its request resolves, but folded
 * later and only by a hook still running (`tick`), so a hook unmounted or hidden while
 * the request was in flight never folded them, and the cursor never moved past them.
 * Where they WERE folded, the fold skips every event at or below the cursor, so a
 * second delivery counts nothing twice.
 *
 * A tagged request that gets no HTTP answer is asked again once, untagged, and the tag
 * dropped: `If-None-Match` needs a CORS preflight, which an older backend (mid-deploy,
 * or after a rollback) refuses, and a kept tag would otherwise fail every poll until a
 * reload.
 */
async function pollRun(declId: string, afterSeq: number): Promise<RunPayload> {
  const kept = lastAnswer?.declId === declId ? lastAnswer : null
  let answer: Awaited<ReturnType<typeof requestRun>>
  try {
    answer = await requestRun(declId, afterSeq, kept?.etag ?? null)
  } catch (error) {
    if (!kept || !noHttpAnswer(error)) throw error
    if (lastAnswer === kept) lastAnswer = null
    answer = await requestRun(declId, afterSeq, null)
  }
  if (answer.status === 304) {
    if (kept) return kept.payload
    // A 304 for a tag this client no longer holds: ask once more, unconditionally.
    answer = await requestRun(declId, afterSeq, null)
    if (answer.status === 304) throw new Error("run poll: 304 without a body")
  }
  const payload = answer.data as RunPayload
  if (answer.etag) lastAnswer = { declId, etag: answer.etag, payload }
  else if (lastAnswer?.declId === declId) lastAnswer = null
  return payload
}

/** `pollRun`, shared: a hook asking while another hook's request for the same
 *  declaration is in flight gets that request's answer. Safe because both fold into
 *  the ONE shared cursor, which skips what is already folded. */
function fetchRun(declId: string, afterSeq: number): Promise<RunPayload> {
  const key = `${declId}:${afterSeq}`
  const pending = inFlightRuns.get(key)
  if (pending) return pending
  const request = pollRun(declId, afterSeq).finally(() => {
    if (inFlightRuns.get(key) === request) inFlightRuns.delete(key)
  })
  inFlightRuns.set(key, request)
  return request
}

/**
 * A row the engine has not finished with. While any exists the run is still moving, so
 * the loop keeps asking even though no cone was ever announced — which is how the
 * INITIAL pass is watched (it emits no bookends, only node events).
 *
 * THIS, AND NOT THE TERMINAL EVENT, IS WHAT STOPS THE LOOP — deliberately. The
 * endpoint's contract tells a client to poll until it sees `run_completed`, and a client
 * that keeps its cursor across mounts cannot: the event is delivered exactly once, and
 * a screen mounted after it has passed would wait for it forever. The matrix answers the
 * same question from STATE, and it cannot be missed: every declared kind is seeded
 * `queued` before the walk starts, so a run in motion always has an unsettled row.
 */
const UNSETTLED = new Set(["queued", "running"])

export function useRunPoll(
  declId: string,
  options?: {
    /** Start watching on mount rather than on a gesture — mission control's mode: it
     *  needs the matrix to render at all, and a live run to follow. */
    watchOnMount?: boolean
    /** KEEP ASKING UNTIL UNMOUNTED (or hidden). The default loop stops after
     *  `GRACE_POLLS` idle pages and on the first failed request — right for a
     *  screen that already holds a matrix. The awaiting-extraction body
     *  (`ExtractionProgress`) mounts BEFORE the worker has seeded any row: an
     *  empty matrix for eight polls, or one 502 from a cold gateway, would
     *  otherwise end the watch for good while the run then proceeds unseen
     *  (review, wave 2). In this mode an idle page reschedules at the
     *  normal cadence and a failed request retries with exponential backoff
     *  (capped); the parent unmounting — which is what a lifecycle leaving
     *  pending/processing does — is the stop. */
    persistent?: boolean
  },
): RunPollState {
  const [nodes, setNodes] = useState<RunNode[]>([])
  const [polled, setPolled] = useState(false)
  const [lockedKinds, setLockedKinds] = useState<Set<string>>(new Set())
  const [changedNodes, setChangedNodes] = useState<Set<string>>(new Set())
  const [inFlight, setInFlight] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [settledCount, setSettledCount] = useState(0)
  const [scopeArticleCount, setScopeArticleCount] = useState<number | null>(
    null,
  )
  const [largeScope, setLargeScope] = useState(false)
  // A monotonically increasing token: bumping it (re)starts the loop below. Ref-free
  // state on purpose — the effect must re-run, and React batches repeated presses.
  const [armed, setArmed] = useState(options?.watchOnMount ? 1 : 0)
  // ARMING BELONGS TO ONE DECLARATION. A route change A -> B keeps the page mounted,
  // and an armed token would follow `declId` straight onto B, which announced nothing.
  // Reset during render (not in an effect), so the poll effect never sees B armed.
  const [armedFor, setArmedFor] = useState(declId)
  if (armedFor !== declId) {
    setArmedFor(declId)
    setArmed(options?.watchOnMount ? 1 : 0)
  }
  // Is this tab actually being looked at? Read at mount rather than assumed, so a run
  // opened into a background tab does not poll for the whole run; a server-rendered
  // pass has no `document` and starts visible.
  const [visible, setVisible] = useState(
    () =>
      typeof document === "undefined" || document.visibilityState === "visible",
  )

  const cursorRef = useRef<RunCursor | null>(null)
  if (cursorRef.current === null || cursorRef.current.declId !== declId) {
    cursorRef.current = cursorFor(declId)
  }
  const cursor = cursorRef.current
  // The cursor's settle count as of this hook's last report. Taken when the hook
  // first watches this cursor and kept across re-arms, so a settle another hook folds
  // between two of this hook's loops is still reported (see `RunCursor.settled`).
  // Settles folded by OTHER hooks before that are not reported; but a cursor nobody
  // has read yet starts at seq 0, so the first loop on it folds the whole history and
  // reports its old settles once — one extra refetch, on purpose cheaper than
  // tracking which settle belongs to which gesture.
  const settledSeen = useRef<{ cursor: RunCursor; seen: number } | null>(null)
  // The live loop's handle, while one is running: a gesture during a loop only
  // resets its silence (no restart, no second request), see `watch`.
  const loopRef = useRef<{ kick: () => void } | null>(null)
  // The cursor's position after this hook's first answer: events at or below it were
  // already in the feed when the hook started watching (see the persistent backoff).
  const history = useRef<{ cursor: RunCursor; seq: number } | null>(null)

  // A gesture while the loop is alive must not restart it: a restart drops the
  // request in flight and asks again at once, and a burst of optimistic toggles
  // would cost one full matrix per click. It only tells the loop to keep listening.
  const watch = useCallback(() => {
    if (loopRef.current) loopRef.current.kick()
    else setArmed((n) => n + 1)
  }, [])
  const persistent = options?.persistent === true

  // TWO LOOPS CAN RUN AT ONCE on one declaration (the footer's approve retry and the
  // page's refetch feed, or a v1 screen and the page). They share one request in
  // flight (`fetchRun`), and the fold below keeps them CORRECT together (duplicate
  // and out-of-order pages are skipped).
  useEffect(() => {
    if (!declId || armed === 0 || !visible) return
    if (settledSeen.current?.cursor !== cursor)
      settledSeen.current = { cursor, seen: cursor.settled }
    const seen = settledSeen.current
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let fetching = false
    let idle = 0
    let failures = 0
    let delay = INTERVAL_MS
    // Wall-clock time of the last gesture (see CEILING_MS). Real elapsed time, not the
    // sum of requested delays, so a slow fetch or a late timer cannot stretch the
    // ceiling.
    let kickedAt = Date.now()
    // A gesture that arrived while a request was in flight. If that request then
    // fails, the gesture's own recompute has nothing watching it, so the loop asks
    // once more instead of stopping (a second failure with no new gesture stops).
    let kickedWhileFetching = false

    const schedule = (ms: number) => {
      timer = setTimeout(tick, ms)
    }
    const stop = () => {
      if (loopRef.current === handle) loopRef.current = null
      if (!cancelled) setWaiting(false)
    }
    const handle = {
      kick: () => {
        idle = 0
        kickedAt = Date.now()
        delay = INTERVAL_MS
        if (fetching) kickedWhileFetching = true
        // Waiting out a long backoff: ask again at the normal cadence instead.
        else {
          if (timer) clearTimeout(timer)
          schedule(INTERVAL_MS)
        }
      },
    }
    loopRef.current = handle

    const tick = async () => {
      if (cancelled) return
      timer = undefined
      let payload: RunPayload
      fetching = true
      kickedWhileFetching = false
      try {
        payload = await fetchRun(declId, cursor.lastSeq)
      } catch {
        fetching = false
        // A failed poll is not a settled cone. Stop rather than spin: the next
        // gesture re-arms, and a screen that stays dim is more honest than one that
        // hammers a failing endpoint. Persistent mode is the exception: it retries
        // with backoff, because nothing re-arms the awaiting body but time.
        if (cancelled) return
        setInFlight(false)
        setWaiting(false)
        if (persistent) {
          failures += 1
          schedule(Math.min(INTERVAL_MS * 2 ** failures, MAX_BACKOFF_MS))
        } else if (kickedWhileFetching) schedule(INTERVAL_MS)
        else stop()
        return
      }
      fetching = false
      if (cancelled) return
      failures = 0

      // A page another loop already folded (same `after_seq`), or one that lands
      // after a newer one, must neither count a settle twice nor reopen a cone, nor
      // move the cursor backwards.
      const from = cursor.lastSeq
      const moved = new Set<string>()
      for (const event of payload.events) {
        if (event.seq <= from) continue
        if (event.kind === "recompute_requested" && event.node_id) {
          const kind = coneKind(event.node_id)
          cursor.requested.set(kind, [
            ...(cursor.requested.get(kind) ?? []),
            event.seq,
          ])
        } else if (event.kind === "recompute_started" && event.cause_node) {
          cursor.open.set(event.cause_node, event.cone ?? [])
          cursor.startedSeq.set(event.cause_node, event.seq)
        } else if (event.kind === "recompute_settled" && event.cause_node) {
          cursor.open.delete(event.cause_node)
          cursor.settled += 1
          // The walk read the declaration when it started: it covers the requests of
          // its kind made before that, never one made after.
          const bound = cursor.startedSeq.get(event.cause_node) ?? event.seq
          cursor.startedSeq.delete(event.cause_node)
          const kind = coneKind(event.cause_node)
          const left = (cursor.requested.get(kind) ?? []).filter(
            (seq) => seq > bound,
          )
          if (left.length) cursor.requested.set(kind, left)
          else cursor.requested.delete(kind)
        } else if (event.kind === "value_changed" && event.node_id) {
          moved.add(event.node_id)
        } else if (event.kind === "run_completed") {
          cursor.completedSeq = Math.max(cursor.completedSeq, event.seq)
        } else if (WALK_EVENTS.has(event.kind)) {
          cursor.walkSeq = Math.max(cursor.walkSeq, event.seq)
        }
      }
      cursor.lastSeq = Math.max(cursor.lastSeq, payload.cursor)

      // New to this cursor: what another loop already folded is not news here.
      const fresh = payload.events.some((e) => e.seq > from)

      const locked = new Set<string>()
      for (const cone of cursor.open.values())
        for (const kind of cone) locked.add(kind)
      setLockedKinds(locked)
      setInFlight(cursor.open.size > 0)
      setWaiting(cursor.open.size > 0 || hasPendingRequest(cursor))
      setNodes(payload.nodes)
      setScopeArticleCount(payload.scope_article_count ?? null)
      setLargeScope(payload.large_scope === true)
      setPolled(true)
      if (moved.size) setChangedNodes((prev) => new Set([...prev, ...moved]))
      // A settled cone means the pipeline re-published, so everything read out of
      // the interface is stale from this instant. Counted rather than flagged so a
      // second settle after the first is a second signal.
      const settled = cursor.settled - seen.seen
      seen.seen = cursor.settled
      if (settled) setSettledCount((n) => n + settled)

      const moving = payload.nodes.some((n) => UNSETTLED.has(n.status))

      // What this hook found already in the feed when it first read it: its history.
      if (history.current?.cursor !== cursor)
        history.current = { cursor, seq: cursor.lastSeq }

      if (persistent) {
        // THE RUN THIS HOOK WATCHED has finished — its `run_completed` arrived after the
        // hook's first read, and no walk event since says a newer run started — and no
        // cone is open: it is not going to move soon, so ask less and less often, 1.2 s
        // doubling to 15 s, instead of every 1.2 s until unmounted. A settled matrix is
        // NOT that signal: a re-extraction is queued over the old run's settled rows,
        // and backing off there would show the new run's start up to 15 s late. So
        // anything else — the old run's history, nothing seeded yet, rows moving — is the
        // normal cadence. (A hook mounted after its run already completed stays at it
        // too, for the moment until the lifecycle leaves pending and unmounts it.)
        const finished =
          cursor.completedSeq > history.current.seq &&
          cursor.completedSeq > cursor.walkSeq
        if (
          !moving &&
          finished &&
          cursor.open.size === 0 &&
          !hasPendingRequest(cursor)
        ) {
          schedule(delay)
          delay = Math.min(delay * 2, MAX_BACKOFF_MS)
        } else {
          delay = INTERVAL_MS
          schedule(INTERVAL_MS)
        }
        return
      }

      if (fresh) idle = 0
      else idle += 1

      // Keep going while a cone is open, a request still waits for its walk, OR the
      // matrix still holds unsettled rows (the initial pass, which announces no cone
      // at all), on the bounded schedule above;
      // otherwise spend the grace and stop. A complete-and-settled run therefore
      // costs eight polls and nothing more.
      let next: number | null = null
      const elapsed = Date.now() - kickedAt
      if (cursor.open.size > 0 || hasPendingRequest(cursor) || moving) {
        if (elapsed >= BACKOFF_PHASE_MS) next = HEARTBEAT_MS
        else {
          next = delay
          delay = Math.min(delay * 2, MAX_BACKOFF_MS)
        }
      } else if (idle < GRACE_POLLS) next = INTERVAL_MS
      if (next !== null && elapsed + next > CEILING_MS) next = null
      if (next === null) stop()
      else schedule(next)
    }

    void tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
      stop()
      setWaiting(false)
    }
  }, [declId, armed, visible, cursor, persistent])

  // A window focus re-arms a hook that has watched before (a reviewer coming back to
  // the tab after a stop); a page nobody has touched stays silent.
  useEffect(() => {
    if (armed === 0) return undefined
    window.addEventListener("focus", watch)
    return () => window.removeEventListener("focus", watch)
  }, [armed, watch])

  // Visibility drives the loop: the effect above re-runs on the flip and takes the one
  // catch-up read for the time spent hidden.
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === "visible")
    document.addEventListener("visibilitychange", onVis)
    return () => document.removeEventListener("visibilitychange", onVis)
  }, [])

  const busy = new Set<string>()
  for (const n of nodes) {
    if (UNSETTLED.has(n.status)) busy.add(`${n.kind} ${n.key}`)
  }

  return {
    nodes,
    scopeArticleCount,
    largeScope,
    polled,
    lockedKinds,
    changedNodes,
    inFlight,
    waiting,
    isInFlight: (kind, key = "") => busy.has(`${kind} ${key}`),
    watch,
    settledCount,
  }
}

/**
 * REFETCH THE INTERFACE-BACKED READS WHEN A CONE SETTLES.
 *
 * A settled cone is the pipeline saying it has re-published. Every read that comes
 * out of the persisted interface is stale from that instant, and nothing else marks
 * it: a gesture invalidates what it can name up front, but a cone's whole point is
 * that it reaches further than the gesture did.
 *
 * It fires on the COUNT, not on a boolean, so two cones settling one after the other
 * are two refetches. The first render is skipped deliberately — mounting is not a
 * settle, and treating it as one would refetch the whole flow on arrival for nothing.
 */
export function useRefetchOnSettled(
  settledCount: number,
  hydrate: () => void,
): void {
  const seen = useRef(settledCount)
  useEffect(() => {
    if (settledCount === seen.current) return
    seen.current = settledCount
    hydrate()
  }, [settledCount, hydrate])
}

/**
 * Is the unit that reads these PUBLISHED FIELDS currently recomputing?
 *
 * The field→kind edge comes from the server (`field_producers`, derived from the
 * declared graph), and which fields a unit renders is a fact about the screen — so
 * neither side holds a copy of the other's knowledge and the mapping cannot drift from
 * the pipeline.
 */
export function unitLocked(
  lockedKinds: Set<string>,
  producers: Record<string, string> | undefined,
  fields: readonly string[],
): boolean {
  if (lockedKinds.size === 0 || !producers) return false
  return fields.some((field) => {
    const kind = producers[field]
    return !!kind && lockedKinds.has(kind)
  })
}

/** The published fields each unit of the resolution surface renders. A UI fact — the
 *  graph edges behind them are the server's (`field_producers`). */
export const UNIT_FIELDS = {
  statement: ["statement_groups", "statement_summaries"],
  scope: ["scope_resolutions", "scope_patterns"],
  cell: ["article_verdicts", "auto_verify", "condition_fill", "adjudication"],
} as const
